/**
 * `AW48` · **La barra de pestañas y el panel que se muestra.** `R1.1 · F3`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐⭐ POR QUÉ LAS CINCO ESTÁN MONTADAS Y SÓLO UNA SE VE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El panel que no está activo se apaga con el atributo `hidden`, **no** dejándolo
 * fuera del árbol. No es una preferencia: `pruebas/aw48-configuracion-del-chatbot.test.tsx`
 * —el examen que ya está en verde y que esta etapa no puede romper— afirma los
 * NUEVE campos por su `id` (`#configuracion-chatbot-<campo>`) **sin navegar**, o
 * sea sobre la pantalla recién montada. Montar los paneles bajo demanda dejaría
 * **ocho** de los nueve fuera del árbol en el estado inicial —el panel de arranque
 * es Personalidad y aporta un solo campo, `personalityProfile`
 * (`pantalla.tsx:407-462`)— y pondría ese examen en rojo, que sería un rojo de
 * esta etapa. El reparto de los nueve (`reglas.ts:97-100`) es: **uno** en
 * Personalidad —`personalityProfile`, el único que no es
 * `CAMPOS_AL_PARTICIPANTE` (`reglas.ts:85-94`)—, **seis** en Mensajes del motor y
 * **dos** en Derivación. Los paneles de Guardrails y Auditoría no aportan ninguno
 * de los nueve `id` que el examen busca.
 *
 * ⚠️ **Y `hidden` es la elección accesible, no un atajo:** el patrón ARIA de
 * pestañas pide que el panel inactivo esté oculto —no ausente— para que el
 * `aria-controls` de cada pestaña apunte a algo que existe. Un panel ausente es un
 * `aria-controls` roto.
 *
 * ⛔ **Las pestañas son ENLACES, no botones.** `CA-14`…`CA-18` dicen «tocar
 * «X» lleva a `#/…`»: es navegación y tiene que sobrevivir al botón «atrás», a
 * abrir en otra pestaña y a copiar la dirección. Un `onClick` que hiciera
 * `history.push` no daría ninguna de las tres.
 */

import { useEffect, useState, type JSX, type ReactNode } from "react"
import {
  PESTANAS,
  ROTULO_DE_LA_PESTANA,
  pestanaDelHash,
  urlDeLaPestana,
  type Pestana,
} from "./rutas.ts"

/** La dirección actual. `""` fuera del navegador. */
function leerElHash(): string {
  return typeof window === "undefined" ? "" : window.location.hash
}

/**
 * **La dirección actual, como estado de React.** ⭐ Una sola fuente para todo lo
 * que se deriva de ella: la pestaña activa y —en Mensajes del motor— la fila
 * abierta.
 *
 * ⚠️ Se escucha `hashchange` y no se usa un estado propio por cada derivación: el
 * estado es **la barra de direcciones**, y con dos `useState` que además empujaran
 * la dirección habría dos verdades sobre lo mismo, y el «atrás» del navegador las
 * separaría.
 *
 * ⛔ **Un solo `useState` y un solo oyente para los dos derivados.** Con un hook
 * por derivación, cada uno abre su propio oyente y una navegación los actualiza
 * por separado: dos renders y, en el medio, un cuadro donde la pestaña activa ya
 * cambió y la fila abierta todavía no.
 */
export function usarElHash(): string {
  const [hash, setHash] = useState<string>(leerElHash)
  useEffect(() => {
    const alCambiar = (): void => {
      setHash(leerElHash())
    }
    window.addEventListener("hashchange", alCambiar)
    return () => {
      window.removeEventListener("hashchange", alCambiar)
    }
  }, [])
  return hash
}

/** La pestaña activa, **leída de la barra de direcciones**. */
export function usarLaPestana(): Pestana {
  return pestanaDelHash(usarElHash())
}

/** La barra de las cinco pestañas, en el orden de `PESTANAS`. `CA-13`. */
export function Pestanas({ activa }: { readonly activa: Pestana }): JSX.Element {
  return (
    <nav
      className="configuracion-chatbot__pestanas"
      aria-label="Secciones de la configuración del chatbot"
    >
      <ul role="tablist" className="configuracion-chatbot__tablist">
        {PESTANAS.map((pestana) => (
          <li key={pestana} role="presentation">
            {/*
              ⛔ `aria-selected` va como string y no como booleano: React omite el
              atributo cuando el valor es `false`, y un `role="tab"` SIN
              `aria-selected` es una pestaña que no declara si está elegida.
            */}
            <a
              id={`configuracion-chatbot-tab-${pestana}`}
              role="tab"
              href={urlDeLaPestana(pestana)}
              aria-selected={pestana === activa ? "true" : "false"}
              aria-controls={`configuracion-chatbot-panel-${pestana}`}
              className={
                pestana === activa
                  ? "configuracion-chatbot__tab configuracion-chatbot__tab--activa"
                  : "configuracion-chatbot__tab"
              }
            >
              {ROTULO_DE_LA_PESTANA[pestana]}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}

/**
 * El panel de una pestaña.
 *
 * ⛔ **Se apaga con `hidden` y no con CSS.** Una regla `.panel{display:none}` deja
 * el panel **dentro del árbol accesible** —y por lo tanto alcanzable con el
 * tabulador y con un lector de pantalla—, que es el defecto que
 * `R1-F4 §5.3` llama «hacer creer que hay un control donde no lo hay». `hidden`
 * no tiene ese problema: saca el subárbol de la accesibilidad, del foco y del
 * orden de tabulación, y no depende de que una hoja de estilos cargue.
 */
export function Panel({
  pestana,
  activa,
  children,
}: {
  readonly pestana: Pestana
  readonly activa: Pestana
  readonly children: ReactNode
}): JSX.Element {
  return (
    <div
      id={`configuracion-chatbot-panel-${pestana}`}
      role="tabpanel"
      aria-labelledby={`configuracion-chatbot-tab-${pestana}`}
      hidden={pestana !== activa}
      className="configuracion-chatbot__panel"
    >
      {children}
    </div>
  )
}
